import { message } from 'antd';

export interface AppError extends Error {
  code?: string;
  status?: number;
  details?: unknown;
}

export class ApiError extends Error implements AppError {
  constructor(
    message: string,
    public code?: string,
    public status?: number,
    public details?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export class ValidationError extends Error implements AppError {
  constructor(
    message: string,
    public code?: string,
    public details?: unknown
  ) {
    super(message);
    this.name = 'ValidationError';
  }
}

/**
 * Handles API errors in a consistent way
 * @param error The error to handle
 * @param customMessage Optional custom message to show to the user
 */
export const handleApiError = (error: unknown, customMessage?: string): void => {
  console.error('API Error:', error);

  if (error instanceof ApiError) {
    switch (error.status) {
      case 400:
        message.error(customMessage || 'Invalid request. Please check your input.');
        break;
      case 401:
        message.error(customMessage || 'Please log in to continue.');
        break;
      case 403:
        message.error(customMessage || 'You do not have permission to perform this action.');
        break;
      case 404:
        message.error(customMessage || 'The requested resource was not found.');
        break;
      case 500:
        message.error(customMessage || 'An internal server error occurred. Please try again later.');
        break;
      default:
        message.error(customMessage || error.message || 'An unexpected error occurred.');
    }
  } else if (error instanceof ValidationError) {
    message.error(customMessage || error.message || 'Validation error occurred.');
  } else {
    message.error(customMessage || 'An unexpected error occurred.');
  }
};

type AxiosLikeError = {
  response?: {
    status?: number;
    data?: { message?: unknown; error?: unknown; field?: unknown };
  };
  message?: unknown;
};

/**
 * Pulls the user-facing message off an axios/fetch/Error rejection.
 * Prefers the server-provided body (`response.data.message` / `error`) over
 * axios's generic "Request failed with status code N".
 */
export const extractApiErrorMessage = (error: unknown, fallback = 'Something went wrong'): string => {
  if (!error) return fallback;
  const e = error as AxiosLikeError;
  const fromBody = e.response?.data;
  if (fromBody) {
    if (typeof fromBody.message === 'string' && fromBody.message.trim()) return fromBody.message;
    if (typeof fromBody.error === 'string' && fromBody.error.trim()) return fromBody.error;
  }
  if (typeof e.message === 'string' && e.message.trim()) return e.message;
  return fallback;
};

/** HTTP status code from an axios-style rejection, if present. */
export const getApiErrorStatus = (error: unknown): number | undefined => {
  const status = (error as AxiosLikeError)?.response?.status;
  return typeof status === 'number' ? status : undefined;
};

/** Server-tagged validation field (e.g. 400 { error, field: 'statusId' }). */
export const getApiErrorField = (error: unknown): string | undefined => {
  const field = (error as AxiosLikeError)?.response?.data?.field;
  return typeof field === 'string' ? field : undefined;
};

/**
 * Safely parses JSON with error handling
 * @param jsonString The JSON string to parse
 * @returns The parsed object or null if parsing fails
 */
export const safeJsonParse = <T>(jsonString: string): T | null => {
  try {
    return JSON.parse(jsonString) as T;
  } catch (error) {
    console.error('JSON Parse Error:', error);
    return null;
  }
};
